import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { useToast } from './ui/use-toast';
import api from '../lib/api';
import { 
  Users, UserPlus, Edit, Trash2, Shield, User as UserIcon, 
  Key, UserCheck, UserX, Wrench, Search, RefreshCw, Copy,
  CheckCircle, XCircle, Smartphone
} from 'lucide-react';

export default function UserManagement({ open, onClose, currentUser }) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    password: '',
    rol: 'encargado',
    encargado_codigo: '',
    activo: true
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch users
  const { data: usersData, isLoading, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(res => res.data),
    enabled: open && currentUser?.rol === 'admin'
  });

  // Fetch encargados from SCMejora for autocomplete
  const { data: encargadosData } = useQuery({
    queryKey: ['encargados'],
    queryFn: () => api.get('/encargados').then(res => res.data),
    enabled: open && currentUser?.rol === 'admin'
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (userData) => api.post('/auth/register', userData),
    onSuccess: (response) => {
      toast({
        title: 'Usuario creado',
        description: `Usuario ${response.data.email} creado correctamente`
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateDialog(false);
      resetForm();
    },
    onError: (error) => {
      toast({
        title: 'Error al crear usuario',
        description: error.response?.data?.detail || 'No se pudo crear el usuario',
        variant: 'destructive'
      });
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }) => api.put(`/users/${userId}`, data),
    onSuccess: () => {
      toast({
        title: 'Usuario actualizado',
        description: 'Los cambios se guardaron correctamente'
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUser(null);
      resetForm();
    },
    onError: (error) => {
      toast({
        title: 'Error al actualizar',
        description: error.response?.data?.detail || 'No se pudo actualizar el usuario',
        variant: 'destructive'
      });
    }
  });

  // Toggle user active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ userId, activo }) => api.put(`/users/${userId}`, { activo }),
    onSuccess: (_, variables) => {
      toast({
        title: variables.activo ? 'Usuario activado' : 'Usuario desactivado',
        description: variables.activo 
          ? 'El usuario puede acceder a la plataforma' 
          : 'El usuario ya no puede acceder a la plataforma'
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo cambiar el estado',
        variant: 'destructive'
      });
    }
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, password }) => api.put(`/users/${userId}/reset-password`, { password }),
    onSuccess: () => {
      toast({
        title: 'Contraseña restablecida',
        description: 'La nueva contraseña ha sido configurada'
      });
      setShowResetPassword(null);
      setNewPassword('');
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo restablecer la contraseña',
        variant: 'destructive'
      });
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId) => api.delete(`/users/${userId}`),
    onSuccess: () => {
      toast({
        title: 'Usuario eliminado',
        description: 'El usuario ha sido eliminado correctamente'
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      toast({
        title: 'Error al eliminar',
        description: error.response?.data?.detail || 'No se pudo eliminar el usuario',
        variant: 'destructive'
      });
    }
  });

  const resetForm = () => {
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      password: '',
      rol: 'encargado',
      encargado_codigo: '',
      activo: true
    });
  };

  const handleCreateUser = () => {
    if (!formData.nombre || !formData.apellido || !formData.email || !formData.password) {
      toast({
        title: 'Campos incompletos',
        description: 'Por favor completa todos los campos obligatorios',
        variant: 'destructive'
      });
      return;
    }
    createUserMutation.mutate(formData);
  };

  const handleUpdateUser = () => {
    const updateData = {
      nombre: formData.nombre,
      apellido: formData.apellido,
      email: formData.email,
      rol: formData.rol,
      encargado_codigo: formData.encargado_codigo,
      activo: formData.activo
    };
    
    if (formData.password) {
      updateData.password = formData.password;
    }
    
    updateUserMutation.mutate({
      userId: editingUser._id,
      data: updateData
    });
  };

  const handleEditUser = (user) => {
    setFormData({
      nombre: user.nombre,
      apellido: user.apellido,
      email: user.email,
      password: '',
      rol: user.rol,
      encargado_codigo: user.encargado_codigo || '',
      activo: user.activo !== false
    });
    setEditingUser(user);
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario? Esta acción no se puede deshacer.')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleToggleActive = (user) => {
    toggleActiveMutation.mutate({
      userId: user._id,
      activo: user.activo === false ? true : false
    });
  };

  const handleResetPassword = () => {
    if (!newPassword || newPassword.length < 6) {
      toast({
        title: 'Contraseña inválida',
        description: 'La contraseña debe tener al menos 6 caracteres',
        variant: 'destructive'
      });
      return;
    }
    resetPasswordMutation.mutate({
      userId: showResetPassword._id,
      password: newPassword
    });
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiado', description: 'Texto copiado al portapapeles' });
  };

  // Filter users
  const filteredUsers = usersData?.users?.filter(user => {
    const matchesSearch = 
      user.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.rol === filterRole;
    
    return matchesSearch && matchesRole;
  }) || [];

  const getRoleBadge = (rol) => {
    switch (rol) {
      case 'admin':
        return <Badge className="bg-purple-600">Administrador</Badge>;
      case 'encargado':
        return <Badge className="bg-orange-500">Encargado</Badge>;
      default:
        return <Badge variant="secondary">Usuario</Badge>;
    }
  };

  if (currentUser?.rol !== 'admin') {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-xl">
              <Shield className="w-6 h-6 mr-2 text-primary" />
              Gestión de Usuarios
            </DialogTitle>
            <DialogDescription>
              Administra los usuarios, roles y permisos de acceso a la plataforma
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Header actions */}
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar usuarios..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="w-40"
                >
                  <option value="all">Todos los roles</option>
                  <option value="admin">Administradores</option>
                  <option value="encargado">Encargados</option>
                  <option value="usuario">Usuarios</option>
                </Select>
                <Button variant="outline" size="icon" onClick={() => refetch()}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <Button
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
                className="bg-[#008040] hover:bg-[#006030]"
                data-testid="create-user-button"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Crear Usuario
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3">
              <Card className="p-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-xl font-bold">{usersData?.users?.length || 0}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Admins</p>
                    <p className="text-xl font-bold">
                      {usersData?.users?.filter(u => u.rol === 'admin').length || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-3">
                <div className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Encargados</p>
                    <p className="text-xl font-bold">
                      {usersData?.users?.filter(u => u.rol === 'encargado').length || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-3">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Activos</p>
                    <p className="text-xl font-bold">
                      {usersData?.users?.filter(u => u.activo !== false).length || 0}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Users list */}
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No se encontraron usuarios</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {filteredUsers.map((user) => (
                  <Card key={user._id} className={`overflow-hidden ${user.activo === false ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className={`p-3 rounded-full ${
                            user.activo === false ? 'bg-gray-200' :
                            user.rol === 'admin' ? 'bg-purple-100' : 
                            user.rol === 'encargado' ? 'bg-orange-100' : 'bg-gray-100'
                          }`}>
                            {user.activo === false ? (
                              <UserX className="w-5 h-5 text-gray-400" />
                            ) : user.rol === 'admin' ? (
                              <Shield className="w-5 h-5 text-purple-600" />
                            ) : user.rol === 'encargado' ? (
                              <Wrench className="w-5 h-5 text-orange-500" />
                            ) : (
                              <UserIcon className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 flex-wrap gap-1">
                              <h3 className="font-semibold">
                                {user.nombre} {user.apellido}
                              </h3>
                              {getRoleBadge(user.rol)}
                              {user.activo === false && (
                                <Badge variant="outline" className="text-red-600 border-red-300">
                                  Inactivo
                                </Badge>
                              )}
                              {user._id === currentUser._id && (
                                <Badge variant="outline" className="text-green-600 border-green-300">
                                  Tú
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                            <div className="flex items-center gap-4 mt-1">
                              {user.encargado_codigo && (
                                <p className="text-xs text-orange-600 flex items-center">
                                  <Wrench className="w-3 h-3 mr-1" />
                                  Código: {user.encargado_codigo}
                                </p>
                              )}
                              {user.created_at && (
                                <p className="text-xs text-muted-foreground">
                                  Creado: {new Date(user.created_at).toLocaleDateString('es-ES')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {user._id !== currentUser._id && (
                          <div className="flex items-center space-x-2">
                            {/* Toggle Active */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleActive(user)}
                              className={user.activo === false ? 'text-green-600' : 'text-red-600'}
                              title={user.activo === false ? 'Activar usuario' : 'Desactivar usuario'}
                            >
                              {user.activo === false ? (
                                <UserCheck className="w-4 h-4" />
                              ) : (
                                <UserX className="w-4 h-4" />
                              )}
                            </Button>
                            {/* Reset Password */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowResetPassword(user)}
                              title="Restablecer contraseña"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            {/* Edit */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              data-testid={`edit-user-${user._id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {/* Delete */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(user._id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${user._id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Info box */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-800">Acceso Móvil para Encargados</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Los usuarios con rol <strong>Encargado</strong> pueden acceder a la vista móvil en:
                    </p>
                    <code className="block mt-2 p-2 bg-white rounded text-sm text-blue-800 font-mono">
                      https://santacruzincidencias.valorizasm.com/mobile
                    </code>
                    <p className="text-xs text-blue-600 mt-2">
                      Asigna un <strong>Código de Encargado</strong> para que solo vean sus incidencias asignadas.
                      Los administradores ven todas las incidencias.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit User Dialog */}
      <Dialog open={showCreateDialog || !!editingUser} onOpenChange={() => {
        setShowCreateDialog(false);
        setEditingUser(null);
        resetForm();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
            </DialogTitle>
            <DialogDescription>
              {editingUser 
                ? 'Modifica los datos del usuario. Deja la contraseña vacía si no quieres cambiarla.'
                : 'Completa los datos para crear un nuevo usuario en la plataforma.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Juan"
                  data-testid="user-nombre"
                />
              </div>
              <div>
                <Label htmlFor="apellido">Apellido *</Label>
                <Input
                  id="apellido"
                  value={formData.apellido}
                  onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                  placeholder="García"
                  data-testid="user-apellido"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="usuario@valorizasm.com"
                data-testid="user-email"
              />
            </div>

            <div>
              <Label htmlFor="password">
                Contraseña {editingUser && '(dejar vacío para no cambiar)'}
                {!editingUser && ' *'}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="password"
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  data-testid="user-password"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
                    let pwd = '';
                    for (let i = 0; i < 10; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
                    setFormData({...formData, password: pwd});
                  }}
                  title="Generar contraseña"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="rol">Rol *</Label>
              <Select
                id="rol"
                value={formData.rol}
                onChange={(e) => setFormData({...formData, rol: e.target.value})}
                data-testid="user-rol"
              >
                <option value="encargado">Encargado (Campo)</option>
                <option value="usuario">Usuario (Lectura)</option>
                <option value="admin">Administrador</option>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {formData.rol === 'admin' && 'Acceso total: crear usuarios, gestionar roles, ver todas las incidencias'}
                {formData.rol === 'encargado' && 'Puede gestionar y actualizar solo las incidencias asignadas'}
                {formData.rol === 'usuario' && 'Solo puede ver incidencias, sin permisos de edición'}
              </p>
            </div>

            {formData.rol === 'encargado' && (
              <div>
                <Label htmlFor="encargado_codigo">Código de Encargado (SCMejora)</Label>
                <Input
                  id="encargado_codigo"
                  value={formData.encargado_codigo}
                  onChange={(e) => setFormData({...formData, encargado_codigo: e.target.value})}
                  placeholder="Ej: JGARCIA, MLOPEZ..."
                  data-testid="user-encargado-codigo"
                  list="encargados-list"
                />
                <datalist id="encargados-list">
                  {encargadosData?.encargados?.map((enc, i) => (
                    <option key={i} value={enc.codigo || enc._id}>
                      {enc.nombre}
                    </option>
                  ))}
                </datalist>
                <p className="text-xs text-muted-foreground mt-1">
                  Este código debe coincidir con el asignado en SCMejora para filtrar las incidencias
                </p>
              </div>
            )}

            {editingUser && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                <div>
                  <Label className="text-sm">Estado del usuario</Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.activo ? 'El usuario puede acceder' : 'El usuario no puede acceder'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${formData.activo ? 'text-green-600' : 'text-red-600'}`}>
                    {formData.activo ? 'Activo' : 'Inactivo'}
                  </span>
                  <Switch
                    checked={formData.activo}
                    onCheckedChange={(checked) => setFormData({...formData, activo: checked})}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setEditingUser(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={editingUser ? handleUpdateUser : handleCreateUser}
              disabled={createUserMutation.isPending || updateUserMutation.isPending}
              className="bg-[#008040] hover:bg-[#006030]"
              data-testid="save-user-button"
            >
              {(createUserMutation.isPending || updateUserMutation.isPending) ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Guardando...
                </>
              ) : (
                editingUser ? 'Guardar Cambios' : 'Crear Usuario'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPassword} onOpenChange={() => {
        setShowResetPassword(null);
        setNewPassword('');
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Key className="w-5 h-5 mr-2" />
              Restablecer Contraseña
            </DialogTitle>
            <DialogDescription>
              Establece una nueva contraseña para {showResetPassword?.nombre} {showResetPassword?.apellido}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nueva Contraseña</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="flex-1"
                />
                <Button variant="outline" onClick={generatePassword} title="Generar">
                  <RefreshCw className="w-4 h-4" />
                </Button>
                {newPassword && (
                  <Button variant="outline" onClick={() => copyToClipboard(newPassword)} title="Copiar">
                    <Copy className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {newPassword && (
                <p className="text-xs text-green-600 mt-1 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Guarda esta contraseña antes de continuar
                </p>
              )}
            </div>

            <div className="bg-yellow-50 p-3 rounded-md text-sm text-yellow-800">
              <strong>Importante:</strong> El usuario deberá usar esta nueva contraseña en su próximo inicio de sesión.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResetPassword(null);
              setNewPassword('');
            }}>
              Cancelar
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.isPending || !newPassword}
              className="bg-[#008040] hover:bg-[#006030]"
            >
              {resetPasswordMutation.isPending ? 'Guardando...' : 'Restablecer Contraseña'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
